import { z } from "zod";

import {
  CHAMPS_PARTICIPANT_MODELE,
  CensusParticipantInputSchema,
  ImportedCensusParticipantSchema,
} from "./census.js";

/** Missing rows/fields are unchanged. Retirement is always an explicit operation. */
export const CensusBatchOperationSchema = z.discriminatedUnion("operation", [
  z
    .object({
      operation: z.literal("add"),
      client_row_id: z.string().uuid(),
      values: ImportedCensusParticipantSchema,
    })
    .strict(),
  z
    .object({
      operation: z.literal("update"),
      client_row_id: z.string().uuid(),
      values: ImportedCensusParticipantSchema,
    })
    .strict(),
  z.object({ operation: z.literal("remove"), client_row_id: z.string().uuid() }).strict(),
]);
export const CensusWorkbookSelectionSchema = z
  .object({
    sheet_name: z.string().min(1).max(200),
    header_row: z.number().int().min(1).max(25),
    columns: z
      .array(
        z
          .object({
            index: z.number().int().min(0).max(63),
            field: z.enum(CHAMPS_PARTICIPANT_MODELE),
          })
          .strict(),
      )
      .min(1)
      .max(64),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      new Set(value.columns.map((c) => c.index)).size !== value.columns.length ||
      new Set(value.columns.map((c) => c.field)).size !== value.columns.length
    )
      ctx.addIssue({ code: "custom", message: "Duplicate column mapping" });
  });
export type CensusWorkbookSelection = z.infer<typeof CensusWorkbookSelectionSchema>;

export const CensusBatchSchema = z
  .object({
    revision: z.string().regex(/^[a-f0-9]{64}$/),
    operations: z.array(CensusBatchOperationSchema).min(1).max(1000),
    source_file_id: z.string().uuid().optional(),
    source_selection: CensusWorkbookSelectionSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (Boolean(value.source_file_id) !== Boolean(value.source_selection))
      ctx.addIssue({ code: "custom", message: "Workbook source and selection required together" });
    const ids = new Set<string>();
    value.operations.forEach((operation, index) => {
      if (ids.has(operation.client_row_id))
        ctx.addIssue({
          code: "custom",
          path: ["operations", index, "client_row_id"],
          message: "Duplicate row operation",
        });
      ids.add(operation.client_row_id);
    });
  });
export type CensusBatch = z.infer<typeof CensusBatchSchema>;
export type CensusBatchOperation = z.infer<typeof CensusBatchOperationSchema>;

/** Use the existing field validators without dropping unreadable original cells. */
export function prepareCensusImportedValues(
  values: Record<string, unknown>,
): Record<string, unknown> {
  const typed: Record<string, unknown> = {};
  const raw: Record<string, string> = {};
  for (const field of CHAMPS_PARTICIPANT_MODELE) {
    if (!Object.hasOwn(values, field)) continue;
    const value = values[field] === "" ? null : values[field];
    const parsed = CensusParticipantInputSchema.shape[field].safeParse(value);
    const isDate = ["birth_date", "hire_date", "coverage_start", "coverage_end"].includes(field);
    const dateValid =
      !isDate ||
      value === null ||
      value === undefined ||
      (typeof value === "string" &&
        !Number.isNaN(Date.parse(value + "T00:00:00Z")) &&
        new Date(value + "T00:00:00Z").toISOString().slice(0, 10) === value);
    if (parsed.success && dateValid) typed[field] = parsed.data;
    else {
      typed[field] = null;
      raw[field] = String(value);
    }
  }
  if (Object.keys(raw).length) typed.import_raw = raw;
  return typed;
}
